import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export const RULE_TRIGGERS = [
  { id: "conversation", label: "During conversation", hint: "AI matches this rule from what the caller says or does." },
];

export function emptyInstructionProfile() {
  return {
    about_you: "",
    global_rules: "",
    your_role: "",
    rules: [],
  };
}

export function normalizeInstructionProfile(raw, legacyInstructions = "") {
  const base = emptyInstructionProfile();
  const src = raw && typeof raw === "object" ? raw : {};
  const about = String(src.about_you || "").trim();
  const global = String(src.global_rules || "").trim();
  const role = String(src.your_role || "").trim();
  const rulesIn = Array.isArray(src.rules) ? src.rules : [];
  const rules = rulesIn.map((r, i) => normalizeRule(r, i)).filter(Boolean);

  const hasStructured = about || global || role || rules.length > 0;
  if (!hasStructured) {
    const legacy = String(legacyInstructions || "").trim();
    if (legacy) {
      return { ...base, global_rules: stripHtmlLite(legacy) };
    }
    return base;
  }
  return {
    about_you: about,
    global_rules: global,
    your_role: role,
    rules,
  };
}

function stripHtmlLite(value) {
  const raw = String(value || "");
  if (!/<[a-z][\s\S]*>/i.test(raw)) return raw;
  return raw
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|h[1-6]|li|tr)\s*>/gi, "\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function newRuleId() {
  return `rule_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function inferRuleTrigger(title = "", when = "", current = "conversation") {
  // Max duration / customer inactivity are configured on the AI Agent node
  // settings (handles), not as Instruction rule triggers.
  return "conversation";
}

export function normalizeRule(raw, index = 0) {
  if (!raw || typeof raw !== "object") return null;
  const title = String(raw.title || "").trim() || `Rule ${index + 1}`;
  const when = String(raw.when || raw.description || "").trim();
  const action = String(raw.action || "hangup").trim().toLowerCase() || "hangup";
  const line = String(raw.line_before_action || raw.line || "").trim();
  const triggerRaw = String(raw.trigger || "conversation").trim().toLowerCase();
  const trigger = inferRuleTrigger(title, when, triggerRaw);
  return {
    id: String(raw.id || newRuleId()),
    title,
    when,
    action,
    line_before_action: line,
    trigger,
  };
}

/** Plain-text instructions block for prompts / legacy consumers. */
export function instructionProfileToText(profile) {
  const p = normalizeInstructionProfile(profile);
  const parts = [];
  if (p.about_you) {
    parts.push("1. About You", p.about_you, "");
  }
  if (p.global_rules) {
    parts.push("2. Global Conversation Rules", p.global_rules, "");
  }
  if (p.your_role) {
    parts.push("3. Your Role", p.your_role, "");
  }
  if (p.rules.length) {
    parts.push("4. List of Rules");
    parts.push(
      "When a rule matches: (1) speak the Line before action, (2) immediately execute the Action. Do not keep chatting after the action."
    );
    p.rules.forEach((r, i) => {
      parts.push("");
      parts.push(`Rule ${i + 1}: ${r.title}`);
      if (r.trigger && r.trigger !== "conversation") {
        parts.push(`Trigger: ${r.trigger}`);
      }
      if (r.when) parts.push(`When: ${r.when}`);
      parts.push(`Line before action: ${r.line_before_action || "(none — speak a brief natural line)"}`);
      parts.push(`Action: ${r.action}`);
    });
  }
  return parts.join("\n").trim();
}

function profilePreviewText(profile) {
  const p = normalizeInstructionProfile(profile);
  const bits = [];
  if (p.about_you) bits.push(p.about_you);
  if (p.global_rules) bits.push(p.global_rules);
  if (p.your_role) bits.push(p.your_role);
  if (p.rules.length) {
    bits.push(`${p.rules.length} rule${p.rules.length === 1 ? "" : "s"}: ${p.rules.map((r) => r.title).join(", ")}`);
  }
  const plain = bits.join(" · ").replace(/\s+/g, " ").trim();
  if (!plain) return "";
  return plain.length > 160 ? `${plain.slice(0, 160)}…` : plain;
}

function profileIsEmpty(profile) {
  const p = normalizeInstructionProfile(profile);
  return !(p.about_you || p.global_rules || p.your_role || p.rules.length);
}

function InstructionProfileForm({
  profile,
  onChange,
  actionList,
}) {
  const [expandedRuleId, setExpandedRuleId] = useState(
    () => (profile.rules.length === 1 ? profile.rules[0].id : null),
  );
  const prevCountRef = useRef(profile.rules.length);

  // One rule stays open. When more than one exist, keep collapsed unless user expands.
  useEffect(() => {
    const n = profile.rules.length;
    const prev = prevCountRef.current;
    prevCountRef.current = n;

    if (n <= 1) {
      setExpandedRuleId(profile.rules[0]?.id || null);
      return;
    }
    setExpandedRuleId((cur) => {
      if (cur && profile.rules.some((r) => r.id === cur)) return cur;
      // Fresh open / invalid id with multiple rules → all collapsed.
      return prev <= 1 ? cur : null;
    });
  }, [profile.rules]);

  const patch = (fields) => onChange({ ...profile, ...fields });

  const updateRule = (id, fields) => {
    onChange({
      ...profile,
      rules: profile.rules.map((r) => (r.id === id ? { ...r, ...fields } : r)),
    });
  };

  const addRule = () => {
    const n = profile.rules.length + 1;
    const defaultAction = actionList.some((a) => a.id === "hangup")
      ? "hangup"
      : (actionList[0]?.id || "hangup");
    const id = newRuleId();
    onChange({
      ...profile,
      rules: [
        ...profile.rules,
        {
          id,
          title: `Rule ${n}`,
          when: "",
          action: defaultAction,
          line_before_action: "",
          trigger: "conversation",
        },
      ],
    });
    // Keep the new rule open so it can be filled in immediately.
    setExpandedRuleId(id);
  };

  const removeRule = (id) => {
    const next = profile.rules.filter((r) => r.id !== id);
    onChange({ ...profile, rules: next });
    setExpandedRuleId((cur) => {
      if (cur !== id) return cur;
      return next.length === 1 ? next[0].id : null;
    });
  };

  const toggleRule = (id) => {
    if (profile.rules.length <= 1) {
      setExpandedRuleId(id);
      return;
    }
    setExpandedRuleId((cur) => (cur === id ? null : id));
  };

  return (
    <div className="space-y-4" data-testid="instruction-profile-form">
      <section className="rounded-md border p-3 space-y-2" data-testid="instr-about-you">
        <Label className="text-xs">1. About You</Label>
        <Textarea
          value={profile.about_you}
          onChange={(e) => patch({ about_you: e.target.value })}
          rows={3}
          placeholder="Who you are on this call (name style, company, tone)…"
          data-testid="instr-about-you-input"
        />
      </section>

      <section className="rounded-md border p-3 space-y-2" data-testid="instr-global-rules">
        <Label className="text-xs">2. Global Conversation Rules</Label>
        <Textarea
          value={profile.global_rules}
          onChange={(e) => patch({ global_rules: e.target.value })}
          rows={4}
          placeholder="Always-on rules: one question at a time, no inventing facts, stay polite…"
          data-testid="instr-global-rules-input"
        />
      </section>

      <section className="rounded-md border p-3 space-y-2" data-testid="instr-your-role">
        <Label className="text-xs">3. Your Role</Label>
        <Textarea
          value={profile.your_role}
          onChange={(e) => patch({ your_role: e.target.value })}
          rows={3}
          placeholder="What you are responsible for on this node (sell, qualify, support)…"
          data-testid="instr-your-role-input"
        />
      </section>

      <section className="rounded-md border p-3 space-y-3" data-testid="instr-rules-list">
        <div className="flex items-start justify-between gap-2">
          <div>
            <Label className="text-xs">4. List of Rules</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Each rule: explain when it applies → choose Action → write the Line spoken before that action.
              {profile.rules.length > 1 ? " Click a rule to expand it." : ""}
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addRule} data-testid="instr-add-rule">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add rule
          </Button>
        </div>

        {!profile.rules.length && (
          <p className="text-[11px] text-muted-foreground border border-dashed rounded-md p-3">
            No rules yet. Add rules for angry callers, not interested, transfer requests, etc.
          </p>
        )}

        <div className="space-y-2">
          {profile.rules.map((rule, index) => {
            const actionLabel = (
              actionList.find((a) => a.id === rule.action)
              || actionList[0]
              || { label: rule.action }
            ).label;
            const isOpen = expandedRuleId === rule.id || profile.rules.length <= 1;
            return (
              <div
                key={rule.id}
                className={`rounded-md border transition-colors ${
                  isOpen
                    ? "border-violet-300 bg-violet-50/80 dark:border-violet-700 dark:bg-violet-950/40"
                    : "border-border bg-muted/15 hover:bg-muted/30"
                }`}
                data-testid={`instr-rule-${index}`}
                data-expanded={isOpen ? "true" : "false"}
              >
                <div className="flex items-center gap-1 px-2.5 py-2">
                  <button
                    type="button"
                    className="flex-1 min-w-0 flex items-center gap-2 text-left rounded-md px-1 py-0.5"
                    onClick={() => toggleRule(rule.id)}
                    data-testid={`instr-rule-toggle-${index}`}
                    aria-expanded={isOpen}
                  >
                    <CaretDown
                      className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${
                        isOpen ? "rotate-0" : "-rotate-90"
                      }`}
                    />
                    <span className={`text-xs font-medium truncate ${isOpen ? "text-violet-900 dark:text-violet-100" : "text-foreground"}`}>
                      Rule {index + 1}: {rule.title || "Untitled"}
                    </span>
                    {!isOpen && (
                      <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">
                        · {actionLabel}
                      </span>
                    )}
                  </button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 shrink-0 text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeRule(rule.id);
                    }}
                    data-testid={`instr-rule-remove-${index}`}
                  >
                    <Trash className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {isOpen && (
                  <div className="px-3 pb-3 space-y-2.5 border-t border-violet-200/70 dark:border-violet-800/60 pt-2.5">
                    <div className="space-y-1.5">
                      <Label className="text-[11px]">Rule name</Label>
                      <Input
                        value={rule.title}
                        onChange={(e) => updateRule(rule.id, { title: e.target.value, trigger: "conversation" })}
                        placeholder="e.g. Angry or Uninterested Prospect"
                        data-testid={`instr-rule-title-${index}`}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-[11px]">When this rule applies</Label>
                      <Textarea
                        value={rule.when}
                        onChange={(e) => updateRule(rule.id, { when: e.target.value, trigger: "conversation" })}
                        rows={2}
                        placeholder="Explain the situation that triggers this rule…"
                        data-testid={`instr-rule-when-${index}`}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-[11px]">2. Action</Label>
                      <Select
                        value={
                          actionList.some((a) => a.id === rule.action)
                            ? rule.action
                            : (actionList[0]?.id || rule.action)
                        }
                        onValueChange={(v) => updateRule(rule.id, { action: v })}
                      >
                        <SelectTrigger data-testid={`instr-rule-action-${index}`}>
                          <SelectValue placeholder="Select action" />
                        </SelectTrigger>
                        <SelectContent>
                          {actionList.map((a) => (
                            <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-[11px]">3. Line before action</Label>
                      <Textarea
                        value={rule.line_before_action}
                        onChange={(e) => updateRule(rule.id, { line_before_action: e.target.value })}
                        rows={2}
                        placeholder="Exact sentence the AI must say before executing the action…"
                        data-testid={`instr-rule-line-${index}`}
                      />
                      <p className="text-[11px] text-muted-foreground">
                        AI speaks this line, then immediately runs the action.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/**
 * Compact Instructions field — Edit opens a modal with the full structured playbook.
 */
export default function InstructionProfileEditor({
  value,
  legacyInstructions = "",
  onChange,
  allowedActions = [],
  actionOptions = [],
}) {
  const [open, setOpen] = useState(false);
  const profile = useMemo(
    () => normalizeInstructionProfile(value, legacyInstructions),
    [value, legacyInstructions],
  );
  const [draft, setDraft] = useState(profile);

  useEffect(() => {
    if (open) setDraft(normalizeInstructionProfile(value, legacyInstructions));
  }, [open, value, legacyInstructions]);

  const actions = (actionOptions || []).filter((a) =>
    !(allowedActions || []).length || allowedActions.includes(a.id)
  );
  const actionList = actions.length
    ? actions
    : (allowedActions || []).map((id) => ({ id, label: id }));

  const empty = profileIsEmpty(profile);
  const preview = profilePreviewText(profile)
    || "Empty — click Edit to write About You, rules, and actions";

  const save = () => {
    const normalized = normalizeInstructionProfile(draft);
    onChange?.(normalized, instructionProfileToText(normalized));
    setOpen(false);
  };

  return (
    <div className="space-y-1.5" data-testid="instruction-profile-editor">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">Instructions</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 rounded-lg text-xs gap-1.5"
          onClick={() => setOpen(true)}
          data-testid="ai-llm-instructions-edit"
        >
          <PencilSimple className="w-3.5 h-3.5" />
          {empty ? "Add" : "Edit"}
        </Button>
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`w-full text-left rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs hover:bg-muted/50 transition-colors min-h-[3.25rem] ${
          empty ? "text-muted-foreground italic" : "text-foreground"
        }`}
        data-testid="ai-llm-instructions-preview"
      >
        <span className="line-clamp-3 whitespace-pre-wrap">{preview}</span>
      </button>
      <p className="text-[11px] text-muted-foreground">
        Structured playbook: About You, global rules, role, and rules (line → action).
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden"
          data-testid="ai-llm-instructions-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b">
            <DialogTitle>Instructions</DialogTitle>
            <DialogDescription>
              Structured playbook the AI Agent must follow. When a rule matches, it speaks the line, then executes the action.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
            <InstructionProfileForm
              profile={draft}
              onChange={setDraft}
              actionList={actionList}
            />
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="ai-llm-instructions-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={save}
              data-testid="ai-llm-instructions-save"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
